// src/components/Common/ProductsTable.js
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Edit, 
  Delete as DeleteIcon
} from '@mui/icons-material';

const ProductsTable = ({ 
  products, 
  categories, 
  colors,
  onEditProduct, 
  onDeleteProduct, 
  getTotalStock, 
  getStockStatus 
}) => {
  // Category display function
  const getCategoryDisplayName = (categoryId) => {
    if (!categoryId) return '';
    
    const category = categories.find(c => c.id === categoryId);
    if (!category) return '';
    
    if (category.parent_id) {
      const parentCategory = categories.find(c => c.id === category.parent_id);
      return parentCategory ? `${parentCategory.name} - ${category.name}` : category.name;
    }
    
    return category.name;
  };

  // Get home color image
  const getHomeColorImage = (product) => {
    if (!product.home_color || !product.home_color.product_color_images) {
      return '/placeholder-image.png';
    }
    
    const sortedImages = product.home_color.product_color_images
      .sort((a, b) => (a.image_order || 0) - (b.image_order || 0));
    
    return sortedImages.length > 0 ? sortedImages[0].image_url : '/placeholder-image.png';
  };

  if (products.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="text.secondary">
          لا توجد منتجات تطابق البحث
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <TableContainer 
        sx={{ 
          direction: 'rtl',
          '& .MuiTable-root': {
            direction: 'rtl',
            minWidth: 650,
            borderCollapse: 'separate',
            borderSpacing: 0
          },
          // Enhanced mobile responsiveness
          '@media (max-width: 900px)': {
            '& .MuiTable-root': {
              minWidth: 'auto'
            }
          },
          // Horizontal scroll on very small screens if needed
          overflowX: 'auto'
        }}
      >
        <Table sx={{ direction: 'rtl' }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'primary.main',
                borderTopRightRadius: '8px',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                المنتج
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'primary.main',
                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                الاسم
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'primary.main',
                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                الفئة
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'primary.main',
                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                حالة المخزون
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                fontWeight: 'bold',
                color: 'white',
                backgroundColor: 'primary.main',
                borderTopLeftRadius: '8px',
                borderRight: '1px solid rgba(255, 255, 255, 0.2)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                الإجراءات
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((product, idx) => {
              const stockStatus = getStockStatus(product);
              const totalStock = getTotalStock(product);
              const homeColorImage = getHomeColorImage(product);
              
              return (
                <TableRow key={product.id} hover>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <Box
                        component="img"
                        src={homeColorImage}
                        alt={product.name_ar}
                        sx={{ 
                          width: { xs: 50, sm: 60, md: 80 }, 
                          height: { xs: 50, sm: 60, md: 80 }, 
                          objectFit: 'cover', 
                          borderRadius: 1,
                          border: '2px solid',
                          borderColor: 'grey.300'
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      {product.name_ar}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    <Typography variant="body2" sx={{ 
                      fontSize: { xs: '0.75rem', sm: '0.875rem' }
                    }}>
                      {getCategoryDisplayName(product.category_id) || product.category}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    <Box
                      sx={{
                        display: 'inline-block',
                        backgroundColor: 
                          stockStatus.color === 'error' ? 'error.main' :
                          stockStatus.color === 'warning' ? 'warning.main' :
                          'success.main',
                        color: 'white',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        boxShadow: 1
                      }}
                    >
                      {stockStatus.text} ({totalStock})
                    </Box>
                  </TableCell>
                  <TableCell sx={{
                    textAlign: 'center',
                    direction: 'rtl',
                    padding: '16px 24px',
                    borderRight: '1px solid rgba(224, 224, 224, 1)',
                    '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                    '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
                  }}>
                    <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                      <Tooltip title="تعديل">
                        <IconButton onClick={() => onEditProduct(product)} color="primary" size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="حذف">
                        <IconButton onClick={() => onDeleteProduct(product.id)} color="secondary" size="small">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ProductsTable;
